import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthStateService } from '../../../core/services/auth-state.service';
import { PushTokenService } from '../../../core/services/push-token.service';
import { APP_VERSION } from '../../../core/constants';
import type {
  AuthSession,
  CheckInviteResponse,
  CompleteInviteRequest,
  CompleteInviteResponse,
  LoginResponse,
  UserProfile,
  VersionCheckResponse,
} from '../../../core/models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authState = inject(AuthStateService);
  private readonly pushToken = inject(PushTokenService);

  async login(email: string, password: string): Promise<void> {
    // Native: send whatever push token initPush() has already cached (may still be
    // null if it hasn't arrived yet — registerFcmToken() covers that case once the
    // 'registration' event fires post-login). Web has no such token, always null.
    const deviceId = Capacitor.isNativePlatform() ? this.pushToken.token() : null;

    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.BASE_URL}mobile/login`, {
        auth: { email, password, device_id: deviceId }
      }),
    );

    const session: AuthSession = {
      token: response.token,
      userRole: response.user_role,
    };

    this.authState.setSession(session);
    await this.fetchProfile();
  }

  /** GET /mobile/app_version — returns the latest version and release notes. */
  checkMobileAppVersion(): Observable<VersionCheckResponse> {
    return this.http.get<VersionCheckResponse>(`${environment.BASE_URL}/version?type=app`);
  }

  /**
   * `silent: true` (used for the best-effort refresh on app open) skips both the
   * global error toast and the forced-logout-on-401 redirect, so a failure (offline,
   * expired token, server hiccup) never disrupts an otherwise-valid cached session —
   * the stale cached profile just keeps being used until a normal, user-initiated
   * action refreshes it (and, if the token really is dead, logs them out then).
   */
  async fetchProfile(options?: { silent?: boolean }): Promise<void> {
    const headers = options?.silent
      ? new HttpHeaders({ 'X-Skip-Error-Toast': '1', 'X-Skip-Auth-Redirect': '1' })
      : undefined;
    const res = await firstValueFrom(
      this.http.get<{ user: UserProfile }>(`${environment.BASE_URL}mobile/profile`, { headers }),
    );
    this.authState.setUserProfile(res.user);
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.delete(`${environment.BASE_URL}mobile/logout/mobile`),
      );
    } catch {
      // Swallow logout errors — session is always cleared.
    } finally {
      this.authState.clearSession();
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${environment.BASE_URL}mobile/recover_account`, {
        action: 'reset_password',
        email,
      }),
    );
  }

  async recoverAccount(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${environment.BASE_URL}mobile/recover_account`, {
        action: 'recover_account',
        email,
      }),
    );
  }

  async registerFcmToken(token: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${environment.BASE_URL}user_message_token`, { token }),
    );
  }

  /** GET mobile/register/:email — looks up a pending invite for prefill. */
  async checkInvite(email: string): Promise<CheckInviteResponse> {
    return firstValueFrom(
      this.http.get<CheckInviteResponse>(
        `${environment.BASE_URL}mobile/register/${encodeURIComponent(email)}`,
      ),
    );
  }

  /** PUT mobile/register/:email — completes the invited user's registration. */
  async completeInvite(body: CompleteInviteRequest): Promise<CompleteInviteResponse> {
    return firstValueFrom(
      this.http.patch<CompleteInviteResponse>(
        `${environment.BASE_URL}mobile/register/${encodeURIComponent(body.email)}`,
        body,
      ),
    );
  }

  /** PATCH mobile/change_password — updates the logged-in user's password. */
  async changePassword(
    currentPassword: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${environment.BASE_URL}mobile/change_password`, {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      }),
    );
  }
}
